import { Box, Flex, IconButton, Text } from "@chakra-ui/react"
import { useRouterState } from "@tanstack/react-router"
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react"
import { FiSend, FiX } from "react-icons/fi"
import { HiSparkles } from "react-icons/hi2"

import { OpenAPI } from "@/client"
import {
  GradientBorder,
  VisionButton,
  VisionInput,
} from "@/components/VisionUI"
import { useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"

interface Msg {
  role: "user" | "assistant"
  text: string
}

// --- Minimal, dependency-free markdown for the assistant's known output subset:
// **bold**, *italic*, `code`, bullet/numbered lists, # headings, paragraphs.
// Renders to React nodes (never dangerouslySetInnerHTML), so it is XSS-safe. ---
const INLINE_RE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\n]+\*)/g

function renderInline(text: string): ReactNode[] {
  return text.split(INLINE_RE).map((part, i) => {
    const key = `${i}:${part}`
    if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key}>{part.slice(2, -2)}</strong>
    }
    if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
      return (
        <Box
          as="code"
          key={key}
          px="0.3em"
          py="0.1em"
          borderRadius="4px"
          bg="rgba(127, 127, 127, 0.18)"
          fontFamily="mono"
          fontSize="0.88em"
        >
          {part.slice(1, -1)}
        </Box>
      )
    }
    if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) {
      return <em key={key}>{part.slice(1, -1)}</em>
    }
    return <Fragment key={key}>{part}</Fragment>
  })
}

const isBullet = (s: string): boolean => /^\s*[-*]\s+/.test(s)
const isNumber = (s: string): boolean => /^\s*\d+\.\s+/.test(s)

function AssistantMarkdown({ text }: { text: string }): ReactNode {
  const lines = text.replace(/\r\n/g, "\n").split("\n")
  const blocks: ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.trim() === "") {
      i += 1
      continue
    }
    const heading = line.match(/^#{1,6}\s+(.*)$/)
    if (heading) {
      blocks.push(
        <Text key={`b${blocks.length}`} fontWeight="bold" mt={2} mb={1}>
          {renderInline(heading[1])}
        </Text>,
      )
      i += 1
      continue
    }
    if (isBullet(line) || isNumber(line)) {
      const ordered = isNumber(line)
      const matches = (s: string) => (ordered ? isNumber(s) : isBullet(s))
      const items: ReactNode[] = []
      while (i < lines.length && matches(lines[i])) {
        const content = lines[i].replace(/^\s*(?:[-*]|\d+\.)\s+/, "")
        items.push(<li key={`i${items.length}`}>{renderInline(content)}</li>)
        i += 1
      }
      blocks.push(
        <Box
          as={ordered ? "ol" : "ul"}
          key={`b${blocks.length}`}
          my={1}
          css={{
            paddingLeft: "1.25em",
            listStyleType: ordered ? "decimal" : "disc",
          }}
        >
          {items}
        </Box>,
      )
      continue
    }
    const para: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !isBullet(lines[i]) &&
      !isNumber(lines[i]) &&
      !/^#{1,6}\s+/.test(lines[i])
    ) {
      para.push(lines[i])
      i += 1
    }
    blocks.push(
      <Text key={`b${blocks.length}`} my={1}>
        {para.map((l, idx) => (
          <Fragment key={`p${idx}`}>
            {idx > 0 && <br />}
            {renderInline(l)}
          </Fragment>
        ))}
      </Text>,
    )
  }
  return <>{blocks}</>
}

/**
 * Floating "Gemini-style" assistant. A sparkle button pinned top-right on every
 * dashboard page opens a popup chat that streams answers from the backend
 * assistant SSE route (which proxies the private Gemma MCP server). Org-scoped:
 * the route enforces READ on the current organisation, and demo visitors are
 * confined to the Demo org.
 */
export function AssistantWidget() {
  const sc = useSemanticColors()
  const { currentOrganization } = useOrganization()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<Msg[]>([])
  const [streaming, setStreaming] = useState(false)
  const [status, setStatus] = useState("")
  const [history, setHistory] = useState<string[]>([])
  // The tag the conversation is drilling into. Set from the backend `focus`
  // event (after a turn resolves a tag) or seeded from the tag-detail page.
  const [focusTag, setFocusTag] = useState<{ id: string; name: string } | null>(
    null,
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  // Shell-style history recall: index into `history` (-1 = editing a fresh
  // draft), and the draft text stashed before navigating into history.
  const histIdx = useRef(-1)
  const draft = useRef("")

  // Page-seed: if the user is on a tag-detail page, focus that tag so "what's
  // its battery?" works with no prior turn. (/tag-detail?tagId=<id>)
  const pageTagId = useRouterState({
    select: (s) =>
      s.location.pathname === "/tag-detail"
        ? ((s.location.search as { tagId?: string })?.tagId ?? null)
        : null,
  })
  useEffect(() => {
    if (pageTagId)
      setFocusTag((cur) =>
        cur?.id === pageTagId ? cur : { id: pageTagId, name: "" },
      )
  }, [pageTagId])

  // No org selected yet → nothing to query.
  if (!currentOrganization) return null

  const scrollToBottom = () =>
    requestAnimationFrame(() =>
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }),
    )

  const appendToLast = (text: string) =>
    setMessages((prev) => {
      const next = [...prev]
      const last = next[next.length - 1]
      if (last && last.role === "assistant") {
        next[next.length - 1] = { ...last, text: last.text + text }
      }
      return next
    })

  const recallHistory = (dir: "up" | "down") => {
    if (history.length === 0) return
    if (dir === "up") {
      if (histIdx.current === -1) {
        draft.current = input
        histIdx.current = history.length - 1
      } else if (histIdx.current > 0) {
        histIdx.current -= 1
      }
      setInput(history[histIdx.current])
    } else {
      if (histIdx.current === -1) return
      if (histIdx.current < history.length - 1) {
        histIdx.current += 1
        setInput(history[histIdx.current])
      } else {
        histIdx.current = -1
        setInput(draft.current)
      }
    }
  }

  const send = async () => {
    const question = input.trim()
    if (!question || streaming) return
    // Recent turns (the conversation before this one) for backend context.
    const priorTurns = messages
      .filter((m) => m.text.trim() !== "")
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.text }))
    setInput("")
    setHistory((prev) => [...prev, question])
    histIdx.current = -1
    draft.current = ""
    setMessages((prev) => [
      ...prev,
      { role: "user", text: question },
      { role: "assistant", text: "" },
    ])
    setStreaming(true)
    setStatus("")
    scrollToBottom()
    try {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${currentOrganization.id}/assistant/stream`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question,
            history: priorTurns,
            focus_tag_id: focusTag?.id,
            focus_tag_name: focusTag?.name || undefined,
          }),
        },
      )
      if (!res.ok || !res.body) {
        appendToLast(`\n[error ${res.status}]`)
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ""
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        // SSE frames are separated by a blank line.
        const frames = buf.split("\n\n")
        buf = frames.pop() ?? ""
        for (const frame of frames) {
          let event = "message"
          let data = ""
          for (const line of frame.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim()
            else if (line.startsWith("data:")) data += line.slice(5).trim()
          }
          if (!data) continue
          try {
            const payload = JSON.parse(data) as {
              text?: string
              message?: string
              tag_id?: string
              name?: string
            }
            if (event === "delta" && payload.text) {
              setStatus("")
              appendToLast(payload.text)
            } else if (event === "status" && payload.text) {
              setStatus(payload.text)
            } else if (event === "focus" && payload.tag_id) {
              setFocusTag({ id: payload.tag_id, name: payload.name ?? "" })
            } else if (event === "error") {
              appendToLast(`\n[error: ${payload.message ?? "unknown"}]`)
            }
          } catch {
            // partial/non-JSON frame — ignore
          }
        }
        scrollToBottom()
      }
    } catch {
      appendToLast("\n[connection error]")
    } finally {
      setStreaming(false)
      setStatus("")
      scrollToBottom()
    }
  }

  return (
    <>
      <IconButton
        aria-label={open ? "Close assistant" : "Open assistant"}
        position="fixed"
        top={{ base: "10px", md: "18px" }}
        right={{ base: "12px", md: "24px" }}
        zIndex={1400}
        borderRadius="full"
        size="lg"
        color="white"
        boxShadow="0 6px 20px rgba(52, 161, 221, 0.45)"
        style={{ background: "linear-gradient(135deg, #34a1dd, #7c5cff)" }}
        _hover={{ transform: "scale(1.05)" }}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <FiX /> : <HiSparkles />}
      </IconButton>

      {open && (
        <GradientBorder
          position="fixed"
          top={{ base: "60px", md: "74px" }}
          right={{ base: "8px", md: "24px" }}
          zIndex={1399}
          borderRadius="20px"
          w={{ base: "calc(100vw - 16px)", md: "420px" }}
        >
          <Flex
            direction="column"
            h={{ base: "62vh", md: "560px" }}
            bg={sc.cardBg}
            borderRadius="20px"
            overflow="hidden"
          >
            <Flex
              align="center"
              gap={2}
              p={4}
              borderBottom="1px solid"
              borderColor={sc.border}
            >
              <HiSparkles color="#34a1dd" />
              <Text fontWeight="bold" color={sc.heading}>
                Ask QDat
              </Text>
              <Text fontSize="xs" color={sc.textMuted} ml="auto">
                {currentOrganization.name}
              </Text>
            </Flex>

            {focusTag && (
              <Flex
                align="center"
                gap={1}
                px={4}
                py={2}
                fontSize="xs"
                bg="rgba(52, 161, 221, 0.12)"
                color={sc.text}
              >
                <Text>
                  Focused: <b>{focusTag.name || "current tag"}</b>
                </Text>
                <Box
                  as="button"
                  ml="auto"
                  aria-label="Clear focus"
                  color={sc.textMuted}
                  _hover={{ color: sc.heading }}
                  onClick={() => setFocusTag(null)}
                >
                  <FiX />
                </Box>
              </Flex>
            )}

            <Box ref={scrollRef} flex={1} overflowY="auto" p={4}>
              {messages.length === 0 ? (
                <Text color={sc.textMuted} fontSize="sm">
                  Ask anything about your tags, readers, alerts, or organisation
                  — in plain language.
                </Text>
              ) : (
                messages.map((m, i) => {
                  let body: ReactNode = ""
                  if (m.text) {
                    body =
                      m.role === "assistant" ? (
                        <AssistantMarkdown text={m.text} />
                      ) : (
                        m.text
                      )
                  } else if (streaming && i === messages.length - 1) {
                    body = status || "…"
                  }
                  return (
                    <Box
                      key={i}
                      mb={3}
                      textAlign={m.role === "user" ? "right" : "left"}
                    >
                      <Box
                        display="inline-block"
                        maxW="85%"
                        px={3}
                        py={2}
                        borderRadius="14px"
                        textAlign="left"
                        whiteSpace={m.role === "user" ? "pre-wrap" : "normal"}
                        fontSize="sm"
                        color={sc.text}
                        bg={
                          m.role === "user"
                            ? "rgba(52, 161, 221, 0.18)"
                            : sc.pageBg
                        }
                      >
                        {body}
                      </Box>
                    </Box>
                  )
                })
              )}
            </Box>

            <Flex
              p={3}
              gap={2}
              borderTop="1px solid"
              borderColor={sc.border}
              align="center"
            >
              <VisionInput
                placeholder="Ask a question…"
                value={input}
                onChange={(e) => {
                  histIdx.current = -1 // editing → leave history navigation
                  setInput(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    void send()
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault()
                    recallHistory("up")
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault()
                    recallHistory("down")
                  }
                }}
              />
              <VisionButton
                aria-label="Send"
                loading={streaming}
                disabled={!input.trim()}
                onClick={() => void send()}
              >
                <FiSend />
              </VisionButton>
            </Flex>
          </Flex>
        </GradientBorder>
      )}
    </>
  )
}
